'use client';

/**
 * Discloses, at compose/send time, that the letter about to go out carries one
 * person's signature over another person's mailbox.
 *
 * Why this exists: banks and registries increasingly ring the solicitor to
 * authenticate correspondence, so the person sending must be able to see that
 * the letter will be signed by one lawyer and delivered from another lawyer's
 * address. Without it, the sender cannot answer "who will they call, and about
 * what?" — and in Outlook's lawyer-identity mode the sending mailbox is the
 * matter's acting solicitor, which is invisible from the composer.
 *
 * This is a DISCLOSURE, not a warning: it never blocks the send, never nags,
 * and is styled as a subordinate caption rather than an alert.
 */
export function SenderSignatoryDisclosure({
  senderName,
  signatoryName
}: {
  /** Display name of the mailbox that will actually send. */
  senderName: string;
  /** Display name of the person whose signature the letter carries. */
  signatoryName: string;
}) {
  return (
    <p className="text-muted-foreground text-xs">
      Sending from {senderName}’s mailbox; letter signed by {signatoryName}.
    </p>
  );
}

/**
 * Resolves the disclosure's props, or null when there is nothing to disclose —
 * Outlook isn't the sender, the sender is unknown, or the signatory and the
 * sending identity are the same person. Name comparison is a deliberate
 * fallback only: callers that can compare identities should pass
 * `sameIdentity` so a name collision never masks a real mismatch.
 */
export function resolveSenderSignatoryDisclosure({
  senderName,
  signatoryName,
  sameIdentity
}: {
  senderName: string | null | undefined;
  signatoryName: string | null | undefined;
  sameIdentity: boolean;
}): { senderName: string; signatoryName: string } | null {
  if (sameIdentity) return null;
  if (!senderName || !signatoryName) return null;
  if (senderName.trim().toLowerCase() === signatoryName.trim().toLowerCase()) {
    return null;
  }
  return { senderName, signatoryName };
}
